import { useEffect, useState } from "react";
import { useFavorite } from "../api/assetlib";
import type { AssetKind } from "../api/types";

export const STAR_TITLE =
  "Favourite: TrueReel prefers your favourites when it writes an ad";

/** The star on anything in the 3D library ([library-section §11.3]). A press
 *  shows the new state at once and puts the old one back if the call fails.
 *  A favourite is the organisation's: the grid can show only those, and
 *  TrueReel prefers them when it writes an ad. */
export function Star({
  kind,
  id,
  on,
  className,
}: {
  kind: AssetKind;
  id: string;
  on: boolean;
  className?: string;
}): JSX.Element {
  const favorite = useFavorite();
  // What the last press asked for, until the list says the same.
  const [asked, setAsked] = useState<boolean | null>(null);
  useEffect(() => setAsked(null), [on, kind, id]);
  const shown = asked ?? on;
  return (
    <button
      type="button"
      aria-pressed={shown}
      aria-label="Favourite"
      title={STAR_TITLE}
      className={[
        "inline-flex h-11 w-11 flex-none cursor-pointer items-center justify-center rounded-full border-0 text-xl leading-none shadow-card",
        shown
          ? "bg-white text-brand-dark"
          : "bg-white/90 text-slate-600 hover:bg-white hover:text-brand-dark",
        className ?? "",
      ].join(" ")}
      onClick={() => {
        if (favorite.isPending) return; // one call at a time, in order
        const next = !shown;
        setAsked(next);
        favorite.mutate(
          { kind, id, on: next },
          { onError: () => setAsked(!next) },
        );
      }}
    >
      <span aria-hidden="true">{shown ? "★" : "☆"}</span>
    </button>
  );
}
